import { useEffect, useCallback, useRef, useMemo } from 'react'
import { useChatStore } from '../../../stores/chatStore'
import { ChatService } from '../../../services/ChatService'
import type { Message } from '../../../types/chat.types'
import { v4 as uuidv4 } from 'uuid'
import { OfflineStorage } from '../../../utils/offlineStorage'

const EMPTY_ARRAY: Message[] = []

/**
 * Industrial-Grade Hook for Chat Message Management
 * Phase 5: Integrated with retry logic and stable UUIDs.
 * 
 * Features:
 * - Comprehensive load state tracking
 * - Request cancellation via AbortController
 * - Error handling with store integration
 * - Memory leak prevention
 * - Stable selectors for performance
 * - Retry logic with stable message IDs
 * - Offline persistence (IndexedDB) - Phase 5.3
 */
export const useChatMessages = (
    agentId: string | null,
    conversationId: string | null,
    userId: string | null,
    onMessageStream?: (messageId: string, conversationId: string, role: string, content: string) => void
) => {
    // Actions (Stable selectors)
    const setMessages = useChatStore(state => state.setMessages)
    const addMessage = useChatStore(state => state.addMessage)
    const setStreamingMessage = useChatStore(state => state.setStreamingMessage)
    const setLoadingMessages = useChatStore(state => state.setLoadingMessages)
    const setLifeCycleState = useChatStore(state => state.setLifeCycleState)
    const updateMessage = useChatStore(state => state.updateMessage)
    const setMessageLoadAttempted = useChatStore(state => state.setMessageLoadAttempted)
    const setMessageLoadError = useChatStore(state => state.setMessageLoadError)
    const setConnectionStatus = useChatStore(state => state.setConnectionStatus)
    const markMessageAsFailed = useChatStore(state => state.markMessageAsFailed)

    // Data Selectors
    const messages = useChatStore(useCallback(state =>
        agentId ? state.agents[agentId]?.messages || EMPTY_ARRAY : EMPTY_ARRAY
        , [agentId]))

    const streamingMessage = useChatStore(useCallback(state =>
        agentId ? state.agents[agentId]?.streamingMessage || null : null
        , [agentId]))

    // Status Selectors
    const isLoaded = useChatStore(useCallback(state =>
        agentId ? state.agents[agentId]?.messagesLoadedForConversationId === conversationId : false
        , [agentId, conversationId]))

    const isLoading = useChatStore(useCallback(state =>
        agentId ? state.agents[agentId]?.isLoadingMessages || false : false
        , [agentId]))

    const loadError = useChatStore(useCallback(state =>
        agentId ? state.agents[agentId]?.messageLoadError || null : null
        , [agentId]))

    // Refs for request management
    const loadingRef = useRef(false)
    const abortControllerRef = useRef<AbortController | null>(null)

    // Load messages via Service
    const loadMessages = useCallback(async () => {
        if (!agentId || !conversationId || loadingRef.current) return

        // Cancel previous request if still pending
        if (abortControllerRef.current) {
            abortControllerRef.current.abort()
        }

        loadingRef.current = true
        abortControllerRef.current = new AbortController()

        // Clear any previous errors when starting a new load
        setMessageLoadError(agentId, null)
        setLoadingMessages(agentId, true)

        try {
            const data = await ChatService.getMessages(conversationId, abortControllerRef.current.signal)

            // PHASE 2.1: Zombie Message Detection
            // Detect messages stuck in 'thinking' state for >2 minutes (server crash)
            const now = Date.now()
            const ZOMBIE_THRESHOLD_MS = 2 * 60 * 1000 // 2 minutes

            const processedMessages = data.map(msg => {
                // Check if message has status field (new schema)
                if ('status' in msg && msg.status === 'thinking') {
                    const createdAt = new Date(msg.created_at).getTime()
                    const ageMs = now - createdAt

                    if (ageMs > ZOMBIE_THRESHOLD_MS) {
                        console.warn(`[useChatMessages] Zombie message detected: ${msg.id} (age: ${Math.round(ageMs / 1000)}s)`)
                        // Mark as failed for UI display
                        return {
                            ...msg,
                            status: 'failed',
                            content: msg.content || 'Erro ao gerar resposta (servidor reiniciou)',
                            failed: true,
                            _isZombie: true // Internal flag for debugging
                        }
                    }
                }
                return msg
            })

            setMessages(agentId, processedMessages, conversationId)
            setMessageLoadAttempted(agentId, conversationId)

        } catch (err: any) {
            // Don't log AbortError (intentional cancellation)
            if (err.name !== 'AbortError') {
                setMessageLoadError(agentId, err as Error)
            }
        } finally {
            setLoadingMessages(agentId, false)
            loadingRef.current = false
            abortControllerRef.current = null
        }
    }, [agentId, conversationId, setMessages, setLoadingMessages, setMessageLoadAttempted, setMessageLoadError])

    // Cleanup on unmount (Phase 3.2 Resilience)
    useEffect(() => {
        return () => {
            if (abortControllerRef.current) {
                abortControllerRef.current.abort()
            }
        }
    }, [])

    // Load effect (Production-Grade Lifecycle)
    useEffect(() => {
        if (agentId && conversationId) {
            if (!isLoaded && !isLoading) {
                loadMessages()
            }
        }
    }, [agentId, conversationId, isLoaded, isLoading, loadMessages])

    // UI Status Selectors (Phase 4.2)
    const lifecycle = useChatStore(useCallback(state =>
        agentId ? state.agents[agentId]?.lifecycle || 'idle' : 'idle'
        , [agentId]))

    const isThinking = lifecycle === 'thinking'

    // Retry state (Phase 5: Retry Logic)
    const lastMessageRef = useRef<{
        conversationId: string
        history: Array<{ role: 'user' | 'assistant'; content: string }>
        assistantMessageId: string
    } | null>(null)

    // Send Message Logic - Service-backed with Retry Support
    const sendMessage = useCallback(async (content: string, _files: unknown[] = [], targetConversationId?: string) => {
        const activeConversationId = targetConversationId || conversationId
        if (!agentId || !userId || !activeConversationId) return

        const tempId = `temp-${Date.now()}`
        const userMessage: Message = {
            id: tempId,
            conversation_id: activeConversationId,
            role: 'user',
            content,
            created_at: new Date().toISOString(),
            user_id: userId,
            agent_id: agentId
        }

        addMessage(agentId, userMessage)
        setLifeCycleState(agentId, 'thinking')

        try {
            // 1. Persist User Message
            const savedUserMsg = await ChatService.saveUserMessage(activeConversationId, content)
            updateMessage(agentId, tempId, savedUserMsg)

            // 2. Prepare History
            const currentAgentState = useChatStore.getState().agents[agentId]
            const currentMessages = currentAgentState?.messages || []

            // Clean history: Only user and assistant messages for now to ensure backend stability
            const history = currentMessages
                .filter(m => m.id !== tempId && (m.role === 'user' || m.role === 'assistant'))
                .map(m => ({
                    role: m.role as 'user' | 'assistant',
                    content: m.content
                }))

            // Add CURRENT message
            history.push({ role: 'user', content: content })

            // ✅ PHASE 5: Generate stable UUID BEFORE stream
            const assistantMessageId = uuidv4()

            // Store for retry
            lastMessageRef.current = {
                conversationId: activeConversationId,
                history,
                assistantMessageId
            }

            console.log(`[useChatMessages] Dispatching stream for agent=${agentId} conv=${activeConversationId} historyLen=${history.length} messageId=${assistantMessageId}`)

            // 3. Initiate Stream with stable ID
            const streamId = `stream-${Date.now()}`
            let fullContent = ''
            let finalId = assistantMessageId // Use stable ID
            setConnectionStatus(agentId, 'connecting')

            await ChatService.streamChat({
                agentId,
                conversationId: activeConversationId,
                messages: history,
                messageId: assistantMessageId, // ✅ Stable ID
                retryAttempt: 0, // ✅ First attempt
                onOpen: () => {
                    setConnectionStatus(agentId, 'connected')
                    setLifeCycleState(agentId, 'streaming')
                    setStreamingMessage(agentId, {
                        id: streamId,
                        role: 'assistant',
                        content: '',
                        isStreaming: true,
                        timestamp: new Date().toISOString()
                    })
                },
                onMessage: (delta, id) => {
                    fullContent += delta
                    finalId = id // Capture the real ID from the stream
                    setStreamingMessage(agentId, {
                        id: streamId,
                        role: 'assistant',
                        content: fullContent,
                        isStreaming: true,
                        timestamp: new Date().toISOString()
                    })
                    if (onMessageStream) {
                        onMessageStream(id, activeConversationId, 'assistant', delta)
                    }

                    // ✅ PHASE 5.3: Save partial content to IndexedDB
                    OfflineStorage.savePartial({
                        id: assistantMessageId,
                        content: fullContent,
                        timestamp: Date.now(),
                        conversationId: activeConversationId,
                        agentId
                    }).catch(err => {
                        console.error('[useChatMessages] Failed to save partial:', err)
                    })
                },
                onError: async (err) => {
                    setConnectionStatus(agentId, 'error', err instanceof Error ? err.message : String(err))
                    markMessageAsFailed(agentId, savedUserMsg.id)

                    // ✅ PHASE 5.3: Try to recover partial content from IndexedDB
                    try {
                        const partial = await OfflineStorage.getPartial(assistantMessageId)
                        if (partial && partial.content.trim()) {
                            console.log(`[useChatMessages] Recovered partial content (${partial.content.length} chars)`)
                            // Add partial content to store as failed message
                            addMessage(agentId, {
                                id: assistantMessageId,
                                conversation_id: activeConversationId,
                                role: 'assistant',
                                content: partial.content + '\n\n[Stream interrupted]',
                                created_at: new Date().toISOString(),
                                isHtml: false,
                                failed: true
                            })
                        }
                    } catch (recoveryErr) {
                        console.error('[useChatMessages] Failed to recover partial:', recoveryErr)
                    }
                },
                onClose: () => {
                    setConnectionStatus(agentId, 'idle')
                    setStreamingMessage(agentId, null)
                    setLifeCycleState(agentId, 'idle')

                    // FINALIZATION: Add the complete message to the persistent store
                    // ✅ GAMBIARRA REMOVED: No more fallback IDs (msg-${Date.now()})
                    // Backend guarantees stable ID via message_id parameter
                    if (fullContent.trim()) {
                        addMessage(agentId, {
                            id: finalId, // Stable ID from backend
                            conversation_id: activeConversationId,
                            role: 'assistant',
                            content: fullContent,
                            created_at: new Date().toISOString(),
                            isHtml: false
                        })
                    }

                    // ✅ PHASE 5.3: Delete partial content after successful completion
                    OfflineStorage.deletePartial(assistantMessageId).catch(err => {
                        console.error('[useChatMessages] Failed to delete partial:', err)
                    })
                },
                signal: abortControllerRef.current?.signal
            })

        } catch (err) {
            setLifeCycleState(agentId, 'error')
            setConnectionStatus(agentId, 'error', err instanceof Error ? err.message : 'Stream failed')

            // ✅ PHASE 2: Outbox Pattern - Queue for offline retry
            if (!navigator.onLine || (err instanceof Error && err.message.includes('network'))) {
                console.log('[useChatMessages] Offline detected, queuing to Outbox')
                useChatStore.getState().addToOutbox({
                    id: tempId,
                    agentId,
                    conversationId: activeConversationId,
                    content,
                    files: _files as any[], // Casting for now
                    retryCount: 0,
                    lastAttempt: Date.now(),
                    status: 'pending'
                })
            }
        } finally {
            // Nothing needed, lifecycle handles it
        }
    }, [agentId, userId, conversationId, addMessage, setLifeCycleState, setStreamingMessage, updateMessage, setConnectionStatus, markMessageAsFailed, onMessageStream])

    // ✅ PHASE 5: Manual Retry Function
    const retryLastMessage = useCallback(async () => {
        if (!agentId || !lastMessageRef.current) {
            console.warn('[useChatMessages] No message to retry')
            return
        }

        const { conversationId: activeConversationId, history, assistantMessageId } = lastMessageRef.current

        console.log(`[useChatMessages] Retrying message messageId=${assistantMessageId}`)

        setLifeCycleState(agentId, 'thinking')
        setConnectionStatus(agentId, 'connecting')

        const streamId = `stream-${Date.now()}`
        let fullContent = ''
        let finalId = assistantMessageId

        try {
            await ChatService.streamChat({
                agentId,
                conversationId: activeConversationId,
                messages: history,
                messageId: assistantMessageId, // ✅ SAME ID for retry
                retryAttempt: 1, // ✅ Retry attempt
                onOpen: () => {
                    setConnectionStatus(agentId, 'connected')
                    setLifeCycleState(agentId, 'streaming')
                    setStreamingMessage(agentId, {
                        id: streamId,
                        role: 'assistant',
                        content: '',
                        isStreaming: true,
                        timestamp: new Date().toISOString()
                    })
                },
                onMessage: (delta, id) => {
                    fullContent += delta
                    finalId = id
                    setStreamingMessage(agentId, {
                        id: streamId,
                        role: 'assistant',
                        content: fullContent,
                        isStreaming: true,
                        timestamp: new Date().toISOString()
                    })
                },
                onError: (err) => {
                    setConnectionStatus(agentId, 'error', err instanceof Error ? err.message : String(err))
                },
                onClose: () => {
                    setConnectionStatus(agentId, 'idle')
                    setStreamingMessage(agentId, null)
                    setLifeCycleState(agentId, 'idle')

                    if (fullContent.trim()) {
                        addMessage(agentId, {
                            id: finalId,
                            conversation_id: activeConversationId,
                            role: 'assistant',
                            content: fullContent,
                            created_at: new Date().toISOString(),
                            isHtml: false
                        })
                    }
                },
                signal: abortControllerRef.current?.signal
            })
        } catch (err) {
            setLifeCycleState(agentId, 'error')
            setConnectionStatus(agentId, 'error', err instanceof Error ? err.message : 'Retry failed')
        }
    }, [agentId, setLifeCycleState, setConnectionStatus, setStreamingMessage, addMessage])

    const sendFeedback = useCallback(async (messageId: string, feedback: 'liked' | 'disliked' | null) => {
        if (!agentId) return
        updateMessage(agentId, messageId, { feedback })
        try {
            await ChatService.updateFeedback(messageId, feedback)
        } catch (err) {
            console.error('[useChatMessages] Feedback failed:', err)
        }
    }, [agentId, updateMessage])

    return useMemo(() => ({
        messages,
        isLoading,
        hasAttemptedLoad: isLoaded, // isLoaded implies attempted load success
        loadError,
        sendMessage,
        streamingMessage,
        sendFeedback,
        isThinking,
        retryLastMessage // ✅ PHASE 5: Manual retry function
    }), [messages, isLoading, isLoaded, loadError, sendMessage, streamingMessage, sendFeedback, isThinking, retryLastMessage])
}
